import { z } from 'zod';

// Common ID validation (32 char hex string)
export const hexIdSchema = z.string().regex(/^[0-9a-f]{32}$/, 'Invalid ID format');

// Optional ID schema for foreign keys
const optionalHexIdSchema = hexIdSchema.optional();

// Study plan session schema for backup validation
const studySessionSchema = z.object({
  subject: z.string(),
  taskTitle: z.string(),
  taskId: z.string().optional(),
  suggestedMinutes: z.number(),
  priority: z.enum(['low', 'medium', 'high']),
  reason: z.string(),
});

// Study day schema for backup validation
const studyDaySchema = z.object({
  date: z.string(),
  dayName: z.string(),
  sessions: z.array(studySessionSchema),
  totalMinutes: z.number(),
});

// Auth validators
export const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
});

export const registerSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
});

// Password reset validators
export const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
});

export const resetPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
  code: z.string().regex(/^\d{6}$/, 'El código debe tener 6 dígitos'),
  newPassword: z.string()
    .min(8, 'La contraseña debe tener al menos 8 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una mayúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
});

// Helper for URL validation that also accepts data: URLs
const urlOrDataUrlSchema = z.string().refine(
  (val) => {
    if (val.startsWith('data:')) return true;
    try {
      new URL(val);
      return true;
    } catch {
      return false;
    }
  },
  { message: 'URL inválida' }
);

// Helper for hex color validation (supports both 3 and 6 char formats)
const hexColorSchema = z.string().regex(
  /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/,
  'Color inválido (formato: #RGB o #RRGGBB)'
);

// Student validators
export const studentSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  grade: z.string().min(1, 'El grado es requerido'),
  schoolName: z.string().min(2, 'El nombre del colegio es requerido'),
  identifyingColor: hexColorSchema,
  avatarUrl: urlOrDataUrlSchema.optional(),
  notes: z.string().optional(),
  birthDate: z.string().optional(),
  evalScale: z.string().max(20).optional(),
});

export const createStudentSchema = studentSchema;
export const updateStudentSchema = studentSchema.partial();

// Task validators
export const taskPrioritySchema = z.enum(['low', 'medium', 'high', 'urgent']);
export const taskStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'cancelled']);
export const taskCategorySchema = z.enum(['homework', 'exam', 'project', 'reading', 'event', 'other']);
export const taskAssigneeSchema = z.enum(['student', 'parent']);

export const recurrenceRuleSchema = z.object({
  frequency: z.enum(['daily', 'weekly', 'monthly']),
  interval: z.number().min(1),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido').optional(),
  daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
}).refine(
  (data) => {
    // daysOfWeek is required when frequency is 'weekly'
    if (data.frequency === 'weekly') {
      return data.daysOfWeek && data.daysOfWeek.length > 0;
    }
    return true;
  },
  {
    message: 'Debes seleccionar al menos un día de la semana para recurrencia semanal',
    path: ['daysOfWeek'],
  }
);

export const attachmentSchema = z.object({
  key: z.string(),
  name: z.string(),
  size: z.number(),
  type: z.string(),
  url: z.string(),
});

export const taskSchema = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().optional(),
  studentId: hexIdSchema.optional(),
  subjectId: hexIdSchema.optional(),
  priority: taskPrioritySchema,
  status: taskStatusSchema,
  assignedTo: taskAssigneeSchema.default('student'),
  dueDate: z.string().optional(),
  dueTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/).optional(),
  reminderAt: z.string().optional(),
  category: taskCategorySchema.optional(),
  recurrence: recurrenceRuleSchema.optional(),
  attachments: z.array(attachmentSchema).optional(),
});

export const createTaskSchema = taskSchema;
export const updateTaskSchema = taskSchema.partial();

// Calendar event validators
export const calendarEventTypeSchema = z.enum([
  'school_day', 'vacation', 'holiday', 'exam', 'meeting', 'activity', 'other'
]);

const calendarEventBaseSchema = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().optional(),
  studentId: hexIdSchema.optional(),
  eventType: calendarEventTypeSchema,
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)').optional(),
  startTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (HH:MM)').optional(),
  endTime: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (HH:MM)').optional(),
  allDay: z.boolean(),
  color: hexColorSchema.optional(),
  recurrence: recurrenceRuleSchema.optional(),
});

export const calendarEventSchema = calendarEventBaseSchema
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.endDate) >= new Date(data.startDate);
    },
    {
      message: 'La fecha de fin debe ser igual o posterior a la fecha de inicio',
      path: ['endDate'],
    }
  )
  .refine(
    (data) => {
      // When allDay is false, startTime and endTime are required
      if (data.allDay === false) {
        return !!data.startTime && !!data.endTime;
      }
      return true;
    },
    {
      message: 'Las horas de inicio y fin son requeridas para eventos que no son de día completo',
      path: ['startTime'],
    }
  );

export const createCalendarEventSchema = calendarEventSchema;
export const updateCalendarEventSchema = calendarEventBaseSchema.partial();

// Subject validators
export const scheduleSlotSchema = z.object({
  day: z.number().min(0).max(6),
  time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Formato de hora inválido (HH:MM)'),
});

export const subjectSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  description: z.string().max(500).optional(),
  studentId: z.string().optional(),
  color: hexColorSchema.optional(),
  scheduleSlots: z.array(scheduleSlotSchema).optional().default([]),
  teacherName: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  specialRequirements: z.string().max(1000, 'Máximo 1000 caracteres').optional().or(z.literal('')),
});

export const createSubjectSchema = subjectSchema;
export const updateSubjectSchema = subjectSchema.partial();

// School year validators
const schoolYearBaseSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  startDate: z.string(),
  endDate: z.string(),
});

export const schoolYearSchema = schoolYearBaseSchema.refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return new Date(data.endDate) > new Date(data.startDate);
  },
  {
    message: 'La fecha de fin debe ser posterior a la fecha de inicio',
    path: ['endDate'],
  }
);

export const schoolYearPartialSchema = schoolYearBaseSchema.partial();

const vacationBaseSchema = z.object({
  schoolYearId: hexIdSchema,
  name: z.string().min(1, 'El nombre es requerido'),
  startDate: z.string(),
  endDate: z.string(),
});

export const vacationSchema = vacationBaseSchema.refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return new Date(data.endDate) >= new Date(data.startDate);
  },
  {
    message: 'La fecha de fin debe ser igual o posterior a la fecha de inicio',
    path: ['endDate'],
  }
);

export const vacationPartialSchema = vacationBaseSchema.partial();

// Period validators (school year periods: trimesters, bimesters, etc.)
const periodBaseSchema = z.object({
  schoolYearId: hexIdSchema,
  name: z.string().min(1, 'El nombre es requerido').max(100),
  number: z.number().min(1, 'El número de periodo debe ser al menos 1').max(6, 'Máximo 6 periodos'),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido'),
});

export const periodSchema = periodBaseSchema.refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return new Date(data.endDate) >= new Date(data.startDate);
  },
  {
    message: 'La fecha de fin debe ser igual o posterior a la fecha de inicio',
    path: ['endDate'],
  }
);

export const createPeriodSchema = periodSchema;
export const updatePeriodSchema = periodBaseSchema.partial();

// Bulk create periods (for generating all periods at once)
export const createPeriodsSchema = z.object({
  schoolYearId: hexIdSchema,
  periodCount: z.number().min(2, 'Mínimo 2 periodos').max(6, 'Máximo 6 periodos'),
  periodNames: z.array(z.string()).optional(),
}).refine(
  (data) => {
    // If periodNames is provided, it must match periodCount
    if (data.periodNames && data.periodNames.length > 0) {
      return data.periodNames.length === data.periodCount;
    }
    return true;
  },
  {
    message: 'El número de nombres de periodo debe coincidir con la cantidad de periodos',
    path: ['periodNames'],
  }
);

// Student-SchoolYear validators
export const studentSchoolYearSchema = z.object({
  studentId: hexIdSchema,
  schoolYearId: hexIdSchema,
  gradeInYear: z.string().optional(),
});

// Notification validators
export const notificationTypeSchema = z.enum(['reminder', 'overdue', 'upcoming', 'uniform', 'info', 'warning']);

export const createNotificationSchema = z.object({
  taskId: optionalHexIdSchema,
  title: z.string().min(1, 'El título es requerido'),
  message: z.string().min(1, 'El mensaje es requerido'),
  type: notificationTypeSchema,
  scheduledFor: z.string().optional(),
});

export const uniformReminderModeSchema = z.enum(['next-day', 'weekly']);

export const notificationPreferencesSchema = z.object({
  overdueTasks: z.boolean(),
  upcomingTasks: z.boolean(),
  upcomingTasksDaysBefore: z.number().int().min(1).max(7),
  uniformReminder: z.boolean(),
  uniformReminderMode: uniformReminderModeSchema,
});

// Uniform validators
export const uniformTypeSchema = z.enum(['daily', 'pe', 'gala', 'jean_day', 'custom']);
export const uniformScheduleModeSchema = z.enum(['weekday', 'day_number']);

const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)');

const uniformBaseSchema = z.object({
  studentId: hexIdSchema,
  type: uniformTypeSchema,
  name: z.string().min(1, 'El nombre es requerido').max(100),
  color: hexColorSchema,
  notes: z.string().max(500).optional(),
});

export const createUniformSchema = uniformBaseSchema;
export const updateUniformSchema = uniformBaseSchema.partial();

export const batchCreateUniformsSchema = z.object({
  studentId: hexIdSchema,
  uniforms: z.array(
    z.object({
      type: uniformTypeSchema,
      name: z.string().min(1).max(100),
      color: hexColorSchema,
      notes: z.string().max(500).optional(),
    })
  ).min(1, 'Debe haber al menos un uniforme'),
});

// assignments keyed by string (JSON-friendly); values are uniform IDs (hex)
const assignmentsRecordSchema = z.record(z.string(), hexIdSchema);
const overridesRecordSchema = z.record(dateStringSchema, hexIdSchema);

export const uniformScheduleUpsertSchema = z.object({
  mode: uniformScheduleModeSchema,
  schoolYearId: hexIdSchema.optional(),
  assignments: assignmentsRecordSchema,
  cycleLength: z.number().int().min(2).max(20).optional(),
  rotationStartDate: dateStringSchema.optional(),
  overrides: overridesRecordSchema.optional(),
}).refine(
  (data) => {
    if (data.mode === 'day_number') {
      if (!data.schoolYearId) return false;
      if (!data.cycleLength) return false;
      // assignments must have exactly cycleLength keys, numbered 1..cycleLength
      const keys = Object.keys(data.assignments);
      if (keys.length !== data.cycleLength) return false;
      for (let i = 1; i <= data.cycleLength; i++) {
        if (!(String(i) in data.assignments)) return false;
      }
    } else {
      // weekday mode: keys must be subset of "0".."6"
      for (const key of Object.keys(data.assignments)) {
        const n = Number(key);
        if (!Number.isInteger(n) || n < 0 || n > 6) return false;
      }
    }
    return true;
  },
  {
    message: 'Asignaciones inválidas para el modo seleccionado',
    path: ['assignments'],
  }
);

export const copyUniformScheduleSchema = z.object({
  fromStudentId: hexIdSchema,
  toStudentId: hexIdSchema,
}).refine((data) => data.fromStudentId !== data.toStudentId, {
  message: 'El estudiante origen y destino deben ser diferentes',
  path: ['toStudentId'],
});

// Holiday validators
const holidayBaseSchema = z.object({
  schoolYearId: hexIdSchema,
  name: z.string().min(1, 'El nombre es requerido').max(100),
  date: dateStringSchema,
});

export const createHolidaySchema = holidayBaseSchema;
export const updateHolidaySchema = holidayBaseSchema.partial();

export const batchCreateHolidaysSchema = z.object({
  schoolYearId: hexIdSchema,
  holidays: z.array(
    z.object({
      name: z.string().min(1).max(100),
      date: dateStringSchema,
    })
  ).min(1, 'Debe haber al menos un festivo'),
});

// AI scan request validators
export const uniformScanRequestSchema = z.object({
  fileKey: z.string().min(1),
  studentId: hexIdSchema,
  mode: uniformScheduleModeSchema,
  cycleLength: z.number().int().min(2).max(20).optional(),
});

export const schoolYearScanRequestSchema = z.object({
  fileKey: z.string().min(1),
});

// Grade validators
export const gradeTypeSchema = z.enum(['acumulativa', 'periodo']);

export const gradeSchema = z.object({
  studentId: hexIdSchema,
  subjectId: optionalHexIdSchema,
  periodId: optionalHexIdSchema,
  name: z.string().min(1, 'El nombre de la evaluación es requerido').max(200),
  gradeType: gradeTypeSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)'),
  gradeLevel: z.string().max(50).optional(),
  score: z.number().min(0, 'La nota no puede ser negativa'),
  maxScore: z.number().min(1, 'La nota máxima debe ser mayor a 0').default(100),
  notes: z.string().max(1000).optional(),
});

export const createGradeSchema = gradeSchema;
export const updateGradeSchema = gradeSchema.partial();

// Type exports from schemas
export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type CreateStudentInput = z.infer<typeof createStudentSchema>;
export type UpdateStudentInput = z.infer<typeof updateStudentSchema>;
export type CreateTaskInput = z.input<typeof createTaskSchema>;
export type UpdateTaskInput = z.input<typeof updateTaskSchema>;
export type CreateCalendarEventInput = z.infer<typeof createCalendarEventSchema>;
export type UpdateCalendarEventInput = z.infer<typeof updateCalendarEventSchema>;
export type CreateSubjectInput = z.infer<typeof createSubjectSchema>;
export type UpdateSubjectInput = z.infer<typeof updateSubjectSchema>;
export type CreateSchoolYearInput = z.infer<typeof schoolYearSchema>;
export type CreateVacationInput = z.infer<typeof vacationSchema>;
export type CreatePeriodInput = z.infer<typeof createPeriodSchema>;
export type UpdatePeriodInput = z.infer<typeof updatePeriodSchema>;
export type CreatePeriodsInput = z.infer<typeof createPeriodsSchema>;
export type CreateStudentSchoolYearInput = z.infer<typeof studentSchoolYearSchema>;
export type CreateNotificationInput = z.infer<typeof createNotificationSchema>;
export type CreateGradeInput = z.infer<typeof createGradeSchema>;
export type UpdateGradeInput = z.infer<typeof updateGradeSchema>;
export type CreateUniformInput = z.infer<typeof createUniformSchema>;
export type UpdateUniformInput = z.infer<typeof updateUniformSchema>;
export type BatchCreateUniformsInput = z.infer<typeof batchCreateUniformsSchema>;
export type UniformScheduleUpsertInput = z.infer<typeof uniformScheduleUpsertSchema>;
export type CopyUniformScheduleInput = z.infer<typeof copyUniformScheduleSchema>;
export type CreateHolidayInput = z.infer<typeof createHolidaySchema>;
export type UpdateHolidayInput = z.infer<typeof updateHolidaySchema>;
export type BatchCreateHolidaysInput = z.infer<typeof batchCreateHolidaysSchema>;
export type UniformScanRequestInput = z.infer<typeof uniformScanRequestSchema>;
export type SchoolYearScanRequestInput = z.infer<typeof schoolYearScanRequestSchema>;

// Backup validators
export const backupRecordCountsSchema = z.object({
  students: z.number(),
  tasks: z.number(),
  subjects: z.number(),
  calendarEvents: z.number(),
  schoolYears: z.number(),
  vacations: z.number(),
  periods: z.number().optional(),
  studentSchoolYears: z.number(),
  notifications: z.number(),
  studyPlans: z.number(),
  attachments: z.number(),
  grades: z.number().optional(),
});

export const backupMetadataSchema = z.object({
  version: z.string(),
  exportedAt: z.string(),
  userEmail: z.string().email(),
  recordCounts: backupRecordCountsSchema,
});

// Relaxed schemas for backup data (allow all fields including id, timestamps, etc.)
const backupStudentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  grade: z.string(),
  schoolName: z.string(),
  identifyingColor: z.string(),
  avatarUrl: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  birthDate: z.string().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const backupTaskSchema = z.object({
  id: z.string(),
  userId: z.string(),
  studentId: z.string().optional().nullable(),
  subjectId: z.string().optional().nullable(),
  title: z.string(),
  description: z.string().optional().nullable(),
  priority: taskPrioritySchema,
  status: taskStatusSchema,
  assignedTo: taskAssigneeSchema.optional().default('student'),
  dueDate: z.string().optional().nullable(),
  dueTime: z.string().optional().nullable(),
  reminderAt: z.string().optional().nullable(),
  category: taskCategorySchema.optional().nullable(),
  attachments: z.array(attachmentSchema).optional().default([]),
  recurrence: recurrenceRuleSchema.optional().nullable(),
  recurrenceSourceId: z.string().optional().nullable(),
  lastRecurrenceDate: z.string().optional().nullable(),
  completedAt: z.string().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough();

const backupSubjectSchema = z.object({
  id: z.string(),
  userId: z.string(),
  studentId: z.string().optional().nullable(),
  name: z.string(),
  description: z.string().optional().nullable(),
  color: z.string(),
  scheduleSlots: z.array(scheduleSlotSchema).optional().default([]),
  teacherName: z.string().optional().nullable(),
  specialRequirements: z.string().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const backupCalendarEventSchema = z.object({
  id: z.string(),
  userId: z.string(),
  studentId: z.string().optional().nullable(),
  title: z.string(),
  description: z.string().optional().nullable(),
  eventType: calendarEventTypeSchema,
  startDate: z.string(),
  endDate: z.string().optional().nullable(),
  startTime: z.string().optional().nullable(),
  endTime: z.string().optional().nullable(),
  allDay: z.boolean(),
  color: z.string().optional().nullable(),
  recurrence: recurrenceRuleSchema.optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const backupSchoolYearSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
});

const backupVacationSchema = z.object({
  id: z.string(),
  userId: z.string(),
  schoolYearId: z.string(),
  name: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  createdAt: z.string(),
});

const backupPeriodSchema = z.object({
  id: z.string(),
  userId: z.string(),
  schoolYearId: z.string(),
  name: z.string(),
  number: z.number(),
  startDate: z.string(),
  endDate: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const backupStudentSchoolYearSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  schoolYearId: z.string(),
  gradeInYear: z.string().optional().nullable(),
  createdAt: z.string(),
});

const backupNotificationSchema = z.object({
  id: z.string(),
  userId: z.string(),
  taskId: z.string().optional().nullable(),
  title: z.string(),
  message: z.string(),
  type: notificationTypeSchema,
  isRead: z.boolean(),
  scheduledFor: z.string().optional().nullable(),
  sentAt: z.string().optional().nullable(),
  createdAt: z.string(),
});

const backupStudyPlanSchema = z.object({
  id: z.string(),
  userId: z.string(),
  studentId: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  plan: z.array(studyDaySchema),
  tips: z.array(z.string()).optional().nullable(),
  daysAhead: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const backupAttachmentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  taskId: z.string().optional().nullable(),
  studentId: z.string().optional().nullable(),
  r2Key: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  aiExtractedData: z.record(z.unknown()).optional().nullable(),
  createdAt: z.string(),
});

const backupGradeSchema = z.object({
  id: z.string(),
  userId: z.string(),
  studentId: z.string(),
  subjectId: z.string().optional().nullable(),
  periodId: z.string().optional().nullable(),
  name: z.string(),
  gradeType: gradeTypeSchema,
  date: z.string(),
  gradeLevel: z.string().optional().nullable(),
  score: z.number(),
  maxScore: z.number(),
  percentage: z.number().optional().nullable(),
  aiExtracted: z.boolean(),
  aiConfidence: z.number().optional().nullable(),
  sourceFileKey: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const backupDataSchema = z.object({
  students: z.array(backupStudentSchema),
  tasks: z.array(backupTaskSchema),
  subjects: z.array(backupSubjectSchema),
  calendarEvents: z.array(backupCalendarEventSchema),
  schoolYears: z.array(backupSchoolYearSchema),
  vacations: z.array(backupVacationSchema),
  periods: z.array(backupPeriodSchema).optional(),
  studentSchoolYears: z.array(backupStudentSchoolYearSchema),
  notifications: z.array(backupNotificationSchema),
  studyPlans: z.array(backupStudyPlanSchema),
  attachments: z.array(backupAttachmentSchema),
  grades: z.array(backupGradeSchema).optional(),
});

export const backupImportSchema = z.object({
  metadata: backupMetadataSchema,
  data: backupDataSchema,
});

export type BackupImportInput = z.infer<typeof backupImportSchema>;

// ============================================
// SUBSCRIPTION VALIDATORS
// ============================================

export const planTierSchema = z.enum(['free', 'premium', 'family']);
export const billingCycleSchema = z.enum(['monthly', 'yearly']);
export const subscriptionStatusSchema = z.enum(['active', 'trialing', 'past_due', 'canceled', 'expired', 'paused']);

export const googlePlayProductIdSchema = z.enum([
  'frappi_premium_monthly',
  'frappi_premium_yearly',
  'frappi_family_monthly',
  'frappi_family_yearly',
]);

export const verifyPurchaseSchema = z.object({
  purchaseToken: z.string().min(1, 'El token de compra es requerido'),
  productId: googlePlayProductIdSchema,
  packageName: z.string().min(1, 'El nombre del paquete es requerido'),
});

export const inviteFamilyMemberSchema = z.object({
  email: z.string().email('Email inválido'),
});

export const acceptFamilyInviteSchema = z.object({
  inviteId: hexIdSchema,
});

// Type exports for subscription validators
export type VerifyPurchaseInput = z.infer<typeof verifyPurchaseSchema>;
export type InviteFamilyMemberInput = z.infer<typeof inviteFamilyMemberSchema>;
export type AcceptFamilyInviteInput = z.infer<typeof acceptFamilyInviteSchema>;

// ============================================
// AI PROVIDER CONFIG VALIDATORS
// ============================================

import { AI_MODELS } from './constants';

export const aiProviderNameSchema = z.enum(['gemini', 'anthropic', 'workers-ai']);

/** Validates a single plan's provider config; model must belong to provider */
export const aiPlanProviderConfigSchema = z
  .object({
    provider: aiProviderNameSchema,
    model: z.string().min(1, 'El modelo es requerido'),
  })
  .superRefine((value, ctx) => {
    const allowed = AI_MODELS[value.provider];
    if (!allowed || !allowed.includes(value.model)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['model'],
        message: `Modelo "${value.model}" no permitido para el proveedor "${value.provider}"`,
      });
    }
  });

/** Validates the full provider config: every plan tier must be present */
export const aiProviderConfigSchema = z.object({
  free: aiPlanProviderConfigSchema,
  premium: aiPlanProviderConfigSchema,
  family: aiPlanProviderConfigSchema,
});

export type AIProviderConfigInput = z.infer<typeof aiProviderConfigSchema>;
